import { Event as RelationlessEvent } from './Event';
import { Participant as RelationlessParticipant } from './Participant';
import { AdditionalInfo, Confirmation, Properties } from './EventIncident';
import { EventDetail } from './EventDetail';
import { Stat } from './Stat';
import { Result } from './Result';
import { SubParticipant } from '@libs/sports-api-resources';
import { SetRequired } from 'type-fest';
import { Standing } from './Standing';

export const enum PushMessageType {
    Event = 'event',
    Incident = 'incident',
    Standings = 'standings',
    StandingsLive = 'standings_live'
}

export interface PushMessage<Data> {
    id: number
    type: PushMessageType
    uuid?: string | null
    source: number
    ut: number
    data: Data
}

export interface UnifiedIncident {
    id: number
    incident_id: number
    participant_id: number | null
    subparticipant_id: number | null
    subparticipant_name: string | null
    info: number | string | null
    add_data: number | string | null
    minute: number | null
    second: number | null
    additional_time?: number
    period_default_duration?: number
    event_status_id: number
    x_pos: number | null
    y_pos: number | null
    home_score: number | string | null
    away_score: number | string | null
    timestamp: number
    ct: number
    ut: number
    attribute_ids: number[]
    confirmation: Confirmation | null
    confirmation_time: number | null
    additional_info: AdditionalInfo
    properties: Properties[]
    action: IncidentAction
}

export const enum IncidentAction {
    Insert = 'insert',
    Update = 'update',
    Delete = 'delete'
}

export interface IncidentData {
    id: string
    action: IncidentAction
    incident_id: number
    participant_id: number | null
    subparticipant_id: number | null
    subparticipant_name: string | null
    info: number | string | null
    add_data: number | string | null
    event_time: string | null
    event_status_id: number
    x_pos: number | null
    y_pos: number | null
    attribute_ids: number[]
    confirmation: Confirmation | null
    confirmation_time: number | null
    ct: number
    ut: number
    additional_info: AdditionalInfo
    properties: Properties[]
}

export type SynchronizedSubParticipant = SetRequired<
    Partial<SubParticipant>,
    | 'id'
    | 'lineup_id'
    | 'team_id'
    | 'short_name'
    | 'acronym'
    | 'area_id'
    | 'area_name'
    | 'area_code'
    | 'stats'
    | 'results'
    | 'ut'
>;
export type MessageSubParticipant = {
    id: number
    acronym: string
    area_code: string
    area_id: number
    area_name: string
    counter: number | null
    name: string
    short_name: string
    ut: number
    participant_id: number | null
    stats: Stat[]
    results: Result[]
};
export type MessageEvent = (RelationlessEvent & {
    details: EventDetail[]
    participants: MessageParticipant[]
});
export type MessageParticipant = (RelationlessParticipant & {
    stats: Stat[]
    results: Result[]
    subparticipants: MessageSubParticipant[]
});
export type MessageStanding = Standing;

export const enum MultipleEventMessageType {
    Full = 'full',
    Diff = 'diff'
}

export type MessageMultipleEvent = {
    event_id: number
    messages: Array<EventPushMessage | IncidentPushMessage>
    type: MultipleEventMessageType
}

export type UnknownPushMessage = PushMessage<object>;
export type EventPushMessage = PushMessage<{ event: MessageEvent }>;
export type IncidentPushMessage = PushMessage<{ incident: IncidentData, event: MessageEvent }>;
export type StandingPushMessage = PushMessage<{ standings: MessageStanding }>;
export type AnyPushMessage = UnknownPushMessage | EventPushMessage | IncidentPushMessage | StandingPushMessage;

export type MultipleEventPushMessage = MessageMultipleEvent[];

export function isSubParticipant(participant: SynchronizedSubParticipant): participant is SubParticipant {
    return !!participant.details;
}

export function isEventMessage(message: AnyPushMessage): message is EventPushMessage {
    return message.type === PushMessageType.Event && 'event' in message.data;
}

export function isIncidentMessage(message: AnyPushMessage): message is IncidentPushMessage {
    return message.type === PushMessageType.Incident && 'incident' in message.data && 'event' in message.data;
}

export function isStandingsLiveMessage(message: AnyPushMessage): message is StandingPushMessage {
    return message.type === PushMessageType.StandingsLive && 'standings' in message.data;
}
